/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {ReactNode} from 'react';
import {Props as RndProps, Rnd} from 'react-rnd';
import {makeStyles} from '@material-ui/core';
import classnames from 'classnames';
import {dlsColor} from '@ion/dls/src/theme/theme';

export interface ResizableDraggableContentProps extends RndProps{
  children: ReactNode;
  defaultSize?: {
    x: number,
    y: number,
    width: number | string,
    height: number | string
  },
  resizeRef?: Rnd;
}

const useStyles = makeStyles(theme => ({
  resizableRoot : {
    backgroundColor : dlsColor('white'),
    transform       : 'translate(0px, 0px) !important'
  }
}));

/**
 * By default we'll allow resizing of the element on the
 * x-axis on the element's right side.
 */
const defaultEnable = {
  top         : false,
  right       : true,
  bottom      : false,
  left        : false,
  topRight    : false,
  bottomRight : false,
  bottomLeft  : false,
  topLeft     : false
};

/**
 * Renders a div element that is resizable and draggable (Dragging disabled
 * by default). If a custom resizeHandleComponent is provided it will be rendered
 * to aid in resizing in addition to the element having a resize icon on the sides
 * that are allowed to be resized. See `defaultEnable` const. Only select props have
 * been exposed from the library to keep things within the DLS.
 *
 * @see https://github.com/bokuweb/react-rnd
 *
 *
 * ```typescript
 *   import {ResizableDraggableContent} from '@ion/dls/src/components/resizable';
 *
 *   <ResizableDraggableContent maxWidth='60%'
 *     bounds='parent'
 *     minWidth='5opx'
 *     onResizeStop={handleResizeStop}
 *     style={{
 *       backgroundColor : 'pink'
 *     }} resizeHandleComponent={{
 *       right : <ResizeGrabber />
 *     }}>
 *     <div>Resize Me</div>
 *   </ResizableDraggableContent>
 */
export const ResizableDraggableContent = ({
  children,
  onResize,
  onResizeStop,
  onResizeStart,
  defaultSize,
  size,
  disableDragging = true,
  minWidth,
  maxWidth,
  minHeight,
  maxHeight,
  bounds,
  enableResizing = defaultEnable,
  dragAxis = 'x',
  resizeHandleComponent,
  lockAspectRatio = false,
  grabberRightIcon = true,
  className,
  style,
  resizeRef,
  resizeHandleClasses
}: ResizableDraggableContentProps) => {
  const classes = useStyles({grabberRightIcon});
  return (
    <Rnd ref={resizeRef}
      onResize={onResize}
      onResizeStop={onResizeStop}
      onResizeStart={onResizeStart}
      default={defaultSize}
      size={size}
      disableDragging={disableDragging}
      minWidth={minWidth}
      maxWidth={maxWidth}
      minHeight={minHeight}
      maxHeight={maxHeight}
      bounds={bounds}
      enableResizing={enableResizing}
      dragAxis={dragAxis}
      resizeHandleComponent={resizeHandleComponent}
      resizeHandleClasses={resizeHandleClasses}
      lockAspectRatio={lockAspectRatio}
      className={classnames(classes.resizableRoot, className)}
      style={style}>
      {children}
    </Rnd>
  );
};

export default ResizableDraggableContent;
